// The Composition tab of a store product: the parts an outfit / kit is made
// of — each a product the store sells on its own — and how the outfit is
// priced.
//
// A part row: the product (picked with the variant-group's ProductPicker),
// its key, its label in the dialog's language, qty, required, which of the
// part's options the buyer picks (Checkbox per option), and of those the
// values allowed (Checkbox per value; all ticked = every value the part
// offers). The tab edits `components` and `compositionPricing` in the form
// (`onChange`), so the dialog's Save writes them; its registered save checks
// them first (a part per row, unique keys).
//
// The total preview is the sum of the parts' current prices (each with its
// own discount, × qty) — what the checkout charges in "sum" mode, less the
// percent in "sum-percent", and the product's own price (Pricing) in "fixed".
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { url } from "aldoo-ra/CMS/url"
import ProductPicker from "../variant-group/ProductPicker"
import { keyFromLabel, textOf, withLabel } from "../variant-group/values"
import { loadParts } from "./api"

const MODES = [
  { value: "sum", name: "Sum of the parts" },
  { value: "sum-percent", name: "Sum of the parts less a percent" },
  { value: "fixed", name: "The product's own price (Pricing)" },
]

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)
const languageCode = (language) => (typeof language === "string" ? language : language?.code || language?.value || "en")
const Label = ({ children }) => <span className="block text-xs font-medium text-admin_text_secondary mb-1">{children}</span>

const money = (amount, currency) => {
  if (!Number.isFinite(amount)) return "—"
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency || "EUR").toUpperCase() }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${String(currency || "").toUpperCase()}`
  }
}

/** A part's price for one unit, its own discount off (minor units). */
const unitPrice = (part) => {
  if (!Number.isFinite(part?.amount)) return null
  const discount = Number(part.discountPercent) > 0 ? Number(part.discountPercent) : 0
  return discount ? Math.round(part.amount * (1 - discount / 100)) : part.amount
}

/** A key no other row has: from the part's name, else part1, part2… */
const freshKey = (wanted, taken) => {
  const base = keyFromLabel(wanted) || "part"
  if (!taken.includes(base)) return base
  let index = 2
  while (taken.includes(`${base}-${index}`)) index++
  return `${base}-${index}`
}

/** A component as it is stored: trimmed, the empty fields left out. */
const cleanComponent = (component) => {
  const out = { key: String(component.key || "").trim(), product: component.product, qty: Math.max(1, Math.round(Number(component.qty) || 1)), required: component.required !== false }
  if (typeof component.label === "string" ? component.label.trim() : isRecord(component.label) && Object.keys(component.label).length) out.label = component.label
  out.choose = Array.isArray(component.choose) ? component.choose.filter(Boolean) : []
  const allowed = Object.fromEntries(Object.entries(isRecord(component.allowed) ? component.allowed : {}).filter(([key, values]) => out.choose.includes(key) && Array.isArray(values) && values.length))
  if (Object.keys(allowed).length) out.allowed = allowed
  return out
}

const problemsOf = (components) => {
  const problems = []
  const keys = components.map((component) => String(component.key || "").trim())
  if (keys.some((key) => !key)) problems.push("every part needs a key")
  if (new Set(keys).size !== keys.length) problems.push("two parts have the same key")
  if (components.some((component) => !component.product)) problems.push("every part needs a product")
  return problems
}

/** One part: the product, key, label, qty, required, the options picked and the values allowed. */
function PartRow({ component, part, index, count, lang, onChange, onRemove, onMove }) {
  const patch = (fields) => onChange({ ...component, ...fields })
  const options = part?.options || []
  const choose = Array.isArray(component.choose) ? component.choose : []
  const allowed = isRecord(component.allowed) ? component.allowed : {}
  const unit = unitPrice(part)
  const qty = Math.max(1, Number(component.qty) || 1)

  const toggleChoose = (key) => patch({ choose: choose.includes(key) ? choose.filter((entry) => entry !== key) : [...choose, key] })
  const toggleValue = (option, key) => {
    const all = option.values.map((value) => value.key)
    const current = allowed[option.key]?.length ? allowed[option.key] : all
    const next = current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    // every value ticked is "all": a value the part gains later comes along
    const narrowed = next.length === all.length ? [] : all.filter((entry) => next.includes(entry))
    patch({ allowed: { ...allowed, [option.key]: narrowed } })
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-admin_bordered p-4" data-testid={`composition-row-${component.key || index}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 shrink-0 rounded overflow-hidden bg-admin_neutral_1">{part?.thumbnail && <img src={url(part.thumbnail)} alt="" className="w-full h-full object-cover" />}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-admin_text line-clamp-1">{textOf(part?.name, lang) || component.product}</div>
          <div className="text-xs text-admin_text_secondary">
            {component.product}
            {unit !== null && ` · ${money(unit, part.currency)}${part.discountPercent > 0 ? ` (−${part.discountPercent}%)` : ""}${qty > 1 ? ` × ${qty}` : ""}`}
          </div>
          {!part && <div className="text-xs text-admin_error">The product is not found.</div>}
          {part && part.available === false && <div className="text-xs text-admin_error">The store does not sell this product now: the outfit cannot be bought.</div>}
          {part?.composition && <div className="text-xs text-admin_error">This product is an outfit itself: one level only, the checkout refuses it.</div>}
        </div>
        <div className="flex items-center">
          <Button variant="admin_icon" title="Open the product" onClick={() => window.open(`?pid=${encodeURIComponent(component.product)}`, "_blank")}>
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </Button>
          <Button variant="admin_icon" title="Up" disabled={index === 0} onClick={() => onMove(index - 1)}>
            <ChevronUpIcon className="w-4 h-4" />
          </Button>
          <Button variant="admin_icon" title="Down" disabled={index === count - 1} onClick={() => onMove(index + 1)}>
            <ChevronDownIcon className="w-4 h-4" />
          </Button>
          <Button variant="admin_icon" title="Remove the part" onClick={onRemove}>
            <TrashIcon className="w-4 h-4 text-admin_error" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-36">
          <Label>Key</Label>
          <Input variant="admin" value={component.key || ""} placeholder="top" onChange={(e) => patch({ key: e.target.value.trim() })} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label>Label ({lang})</Label>
          <Input variant="admin" value={textOf(component.label, lang)} placeholder={textOf(part?.name, lang) || "T-shirt"} onChange={(e) => patch({ label: withLabel(component.label, lang, e.target.value) })} />
        </div>
        <div className="w-24">
          <Label>Qty</Label>
          <Input variant="admin" type="number" value={component.qty ?? 1} onChange={(e) => patch({ qty: Math.max(1, Math.round(Number(e.target.value) || 1)) })} />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <ToggleSwitch variant="admin" isOn={component.required !== false} onChange={(on) => patch({ required: !!on })} />
          <span className="text-sm">Required</span>
          <span className="text-xs text-admin_text_secondary">{component.required !== false ? "always in the outfit" : "the buyer may add it"}</span>
        </div>
      </div>

      {part && (
        <div className="flex flex-col gap-3">
          <Label>The buyer picks</Label>
          {!options.length && <Typography variant="admin_body2" className="opacity-70">The product has no options: nothing to pick.</Typography>}
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {options.map((option) => (
              <Checkbox key={option.key} variant="admin" label={`${option.label || option.key}${option.variantAxis ? " (variant)" : ""}`} isOn={choose.includes(option.key)} onChange={() => toggleChoose(option.key)} />
            ))}
          </div>
          {options
            .filter((option) => choose.includes(option.key))
            .map((option) => {
              const narrowed = allowed[option.key]?.length ? allowed[option.key] : null
              return (
                <div key={option.key} className="flex flex-col gap-1">
                  <Label>
                    {option.label || option.key}: allowed values {narrowed ? `(${narrowed.length} of ${option.values.length})` : "(all)"}
                  </Label>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {option.values.map((value) => (
                      <Checkbox key={value.key} variant="admin" label={value.label && value.label !== value.key ? `${value.key} · ${value.label}` : value.key} isOn={!narrowed || narrowed.includes(value.key)} onChange={() => toggleValue(option, value.key)} />
                    ))}
                  </div>
                </div>
              )
            })}
          {options.some((option) => option.variantAxis && !choose.includes(option.key)) && (
            <Typography variant="admin_body2" className="text-admin_text_warning">A variant option the buyer does not pick leaves the part without a SKU: the checkout refuses it.</Typography>
          )}
        </div>
      )}
    </div>
  )
}

export default function CompositionTab({ value, product, dataItem, onChange, register, language }) {
  const lang = languageCode(language)
  const pid = value?.pid || (dataItem || product)?.pid
  const components = useMemo(() => (Array.isArray(value?.components) ? value.components : []), [value?.components])
  const pricing = isRecord(value?.compositionPricing) ? value.compositionPricing : {}
  const mode = MODES.some((entry) => entry.value === pricing.mode) ? pricing.mode : "sum"
  const [parts, setParts] = useState({})
  const [error, setError] = useState(null)
  const [picking, setPicking] = useState(false)

  // the parts' details (name, picture, price, options), one request for those not loaded yet
  const pidsKey = [...new Set(components.map((component) => component.product).filter(Boolean))].join(",")
  useEffect(() => {
    const missing = pidsKey ? pidsKey.split(",").filter((entry) => !(entry in parts)) : []
    if (!missing.length) return
    let alive = true
    loadParts(missing, lang)
      .then((found) => {
        if (!alive) return
        const byPid = Object.fromEntries(missing.map((entry) => [entry, null]))
        for (const part of found) byPid[part.pid] = part
        setParts((current) => ({ ...current, ...byPid }))
      })
      .catch((e) => alive && setError(e?.message || "Failed to load the parts"))
    return () => {
      alive = false
    }
  }, [pidsKey, lang])

  const latest = useRef(null)
  latest.current = { components, pricing }
  useEffect(() => {
    register?.({
      save: async () => {
        const { components: current, pricing: currentPricing } = latest.current
        if (!current.length) return {}
        const problems = problemsOf(current)
        if (problems.length) {
          showNotification({ message: `Composition: ${problems.join(". ")}`, className: "bg-admin_error text-white" })
          return false
        }
        const percent = Math.min(100, Math.max(0, Number(currentPricing.percent) || 0))
        return { components: current.map(cleanComponent), compositionPricing: { mode: currentPricing.mode || "sum", ...(currentPricing.mode === "sum-percent" ? { percent } : {}) } }
      },
    })
    return () => register?.(null)
  }, [])

  const setComponents = (next) => onChange?.({ components: next })
  const setPricing = (fields) => onChange?.({ compositionPricing: { mode, ...(pricing.percent !== undefined ? { percent: pricing.percent } : {}), ...fields } })
  const move = (from, to) => {
    if (to < 0 || to >= components.length) return
    const next = [...components]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row)
    setComponents(next)
  }

  /** New rows for the picked products; the variant options are what the buyer picks at first. */
  const addParts = async (pids) => {
    let found = []
    try {
      found = await loadParts(pids.filter((entry) => !(entry in parts)), lang)
    } catch (e) {
      setError(e?.message || "Failed to load the parts")
    }
    const known = { ...parts, ...Object.fromEntries(found.map((part) => [part.pid, part])) }
    setParts(known)
    const taken = components.map((component) => component.key)
    const rows = pids.map((entry) => {
      const part = known[entry]
      const key = freshKey(textOf(part?.name, "en") || entry, taken)
      taken.push(key)
      return { key, product: entry, qty: 1, required: true, choose: (part?.options || []).filter((option) => option.variantAxis).map((option) => option.key), allowed: {} }
    })
    setComponents([...components, ...rows])
  }

  // the live total: each part's current price × qty, by the mode
  const priced = components.filter((component) => component.required !== false)
  const sum = priced.reduce((total, component) => total + (unitPrice(parts[component.product]) ?? 0) * Math.max(1, Number(component.qty) || 1), 0)
  const optional = components.filter((component) => component.required === false)
  const optionalSum = optional.reduce((total, component) => total + (unitPrice(parts[component.product]) ?? 0) * Math.max(1, Number(component.qty) || 1), 0)
  const currency = parts[components[0]?.product]?.currency
  const percent = Math.min(100, Math.max(0, Number(pricing.percent) || 0))
  const total = (amount) => (mode === "sum-percent" ? Math.round(amount * (1 - percent / 100)) : amount)
  const selectedMode = MODES.find((entry) => entry.value === mode)

  return (
    <div className="flex flex-col gap-6" data-testid="composition-section">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Typography component="h4" variant="admin_h5" className="my-0">
            Parts
          </Typography>
          <Button variant="admin_secondary" onClick={() => setPicking(true)} data-testid="composition-add">
            <PlusIcon className="w-4 h-4" /> Add parts
          </Button>
        </div>
        <Typography variant="admin_body2" className="opacity-70">
          The products this one is made of. Each is sold on its own as well; the buyer picks its options here, and the stock is taken from the part.
        </Typography>
        {error && <Typography variant="admin_body2" className="text-admin_error">{error}</Typography>}
        {!components.length && <Typography variant="admin_body2" className="opacity-70">No parts: the product is not an outfit.</Typography>}
        {components.map((component, index) => (
          <PartRow
            key={`${component.key}-${index}`}
            component={component}
            part={parts[component.product]}
            index={index}
            count={components.length}
            lang={lang}
            onChange={(next) => setComponents(components.map((entry, at) => (at === index ? next : entry)))}
            onRemove={() => setComponents(components.filter((_, at) => at !== index))}
            onMove={(to) => move(index, to)}
          />
        ))}
      </div>

      {components.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-admin_bordered p-4" data-testid="composition-pricing">
          <Typography component="h4" variant="admin_h5" className="my-0">
            Price
          </Typography>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-80">
              <Label>Priced as</Label>
              <DropdownSelect
                variant="admin"
                options={MODES}
                value={selectedMode ? [selectedMode] : []}
                allowEmptySelection={false}
                enableResetButton={false}
                showItemNamesInSelection
                showCheckbox={false}
                showSelectionTick
                onChange={(picked) => {
                  const option = Array.isArray(picked) ? picked[0] : picked
                  if (option) setPricing({ mode: option.value })
                }}
              />
            </div>
            {mode === "sum-percent" && (
              <div className="w-32">
                <Label>Less (%)</Label>
                <Input variant="admin" type="number" value={pricing.percent ?? ""} placeholder="10" onChange={(e) => setPricing({ percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })} />
              </div>
            )}
          </div>
          <div className="text-sm" data-testid="composition-total-preview">
            {mode === "fixed" ? (
              <span>
                The outfit costs its own price (Pricing tab). The parts would come to {money(sum, currency)}.
              </span>
            ) : (
              <span>
                Total now: <strong>{money(total(sum), currency)}</strong>
                {mode === "sum-percent" && percent > 0 && ` (${money(sum, currency)} less ${percent}%)`}
                {optional.length > 0 && `; with every optional part ${money(total(sum + optionalSum), currency)}`}. The product's own discount comes off this; the parts' discounts are in their prices.
              </span>
            )}
          </div>
        </div>
      )}

      <ProductPicker
        isOpen={picking}
        onClose={() => setPicking(false)}
        onPick={addParts}
        exclude={pid ? [pid] : []}
        title="Add parts to the outfit"
        excludedLabel="this product"
      />
    </div>
  )
}
